import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import ComponentTable from '../../../Components/Table/Index';
import { Column } from '../../../Components/Table/Types';
import * as Icon from '@mui/icons-material/';
import { useNavigate } from 'react-router-dom';
import { CondicaoPagamentoAtualizar, CondicaoPagamentoCadastrar } from '../../../../routes/RoutesNames';
import { useSelector } from 'react-redux';
import CondicaoPagamentoController from '../../../../controllers/CondicaoPagamentoController';
import { Button } from '../../../../shared/components/button/Button';
import { HeaderComponent } from '../../../../shared/components/HeaderComponent';
import { toast } from 'react-toastify';
import { ButtonMenu } from '../../../../shared/components/ButtonMenu';
import FormBuilder from '../../../../shared/components/FormBuilder';

interface Row {
  cpg_id: number;
  cpg_descricao: string;
  cpg_tipo_utilizacao: string;
  cpg_tipo_parcelamento: string;
  cpg_quantidade: string;
  cpg_dias_parcelas: string;
  BtnMenu?: JSX.Element;
}

export default function CondicaoPagamento() {
  const ref = useRef<HTMLButtonElement>(null);
  const userId = useSelector((state: any) => state.Authentication.userId);
  const navigate = useNavigate();

  // Tipo Utilização e Tipo Parcelamento saíram do grid e dos filtros: são códigos
  // legados que nenhuma proc lê. Quem define o vencimento é "Dias das Parcelas".
  const tableColumns: Column[] = [
    { id: 'Id', label: 'Id', minWidth: 0, align: 'center' },
    { id: 'Descricao', label: 'Descrição', minWidth: 0 },
    { id: 'Quantidade', label: 'Quantidade', minWidth: 0, align: 'center' },
    { id: 'DiasParcelas', label: 'Dias das Parcelas', minWidth: 0, align: 'center' },
    { id: 'BtnMenu', label: '', width: 60, minWidth: 0, align: 'right' },
  ];

  const fields = [
    { name: 'CondicaoPagamentoId', type: 'text', label: 'Código' },
    { name: 'Descricao', type: 'text', label: 'Descrição' },
    { name: 'Quantidade', type: 'text', label: 'Quantidade' },
    { type: 'submit', icon: (<Icon.Search style={{ fontSize: '28px' }} />) },
  ]

  // TipoUtilizacao e TipoParcelamento continuam aqui, mesmo sem campo na tela: a
  // PROC_CONDICAOPAGAMENTO_LISTAR compara com LIKE '%'+@param+'%' e, se chegarem
  // NULL, a condição inteira vira NULL e a listagem não devolve linha nenhuma.
  const initialValues = {
    CondicaoPagamentoId: null,
    Descricao: '',
    TipoUtilizacao: '',
    TipoParcelamento: '',
    Quantidade: '',
  };

  const [tableRows, setTableRows] = useState<Row[]>([]);

  useEffect(() => {
    ref?.current?.click();
  }, []);

  const handleNavigation = async (): Promise<void> => {
    navigate(CondicaoPagamentoCadastrar);
  };

  const handleEditItem = (item: Row, duploClick = false, duploClickId = 0) => {
    if (duploClick) {
      navigate(CondicaoPagamentoAtualizar.replace(':id', duploClickId.toString()), { state: { duploClick: true } });
    }
    else {
      navigate(CondicaoPagamentoAtualizar.replace(':id', item.cpg_id.toString()),);
    }
  };

  const handleDeleteItem = async (cpg_id: number) => {
    const request: any = { UsuId: userId, CondicaoPagamentoId: cpg_id };
    const response = await CondicaoPagamentoController.Excluir(request);

    if (response.StatusCode === 204) {
      toast('Condição de Pagamento excluído com sucesso!', {
        type: 'success',
      });
    } else {
      toast('Erro ao excluir a condição de pagamento!', {
        type: 'error',
      });
    }
    await handleSubmit(initialValues)
  };

  const handleSubmit = async (values: any) => {
    const DataRows: any = [];
    const Response = await CondicaoPagamentoController.Listar(values);

    if (Response.StatusCode === 200 && Response?.Data?.length > 0) {
      Response.Data.map((item: Row) => {
        DataRows.push({         
          Id: item.cpg_id,
          Descricao: item.cpg_descricao,
          Quantidade: item.cpg_quantidade,
          DiasParcelas: item.cpg_dias_parcelas,
           BtnMenu: (
            <ButtonMenu
              items={[
                { onClick: () => handleEditItem(item), menu: 'Editar' },
                {
                  onClick: () => handleDeleteItem(item.cpg_id),
                  menu: 'Excluir', confirmar: `Excluir a condição de pagamento "${item.cpg_descricao ?? ''}"? Esta ação não pode ser desfeita.`,
                },
              ]}
            />
          ),
        });

        setTableRows(DataRows);
      });
    } else if (Response.StatusCode === 204 && Response?.Data?.length === 0) {
      toast('Não há registros cadastrados.', {
        type: 'warning',
      });
    } else {
      toast(Response?.Data, {
        type: 'warning',
      });
    }
  };

  useEffect(() => {
    handleSubmit(initialValues);
  }, []);

  return (
    <Box>
      <HeaderComponent text={'Condição de Pagamento'}
        ButtonRight={
          <Button
            variant='success'
            onClick={handleNavigation}
            iconStart={<Icon.Add />}
            title={'Incluir'}
          />
        }
      />
      <FormBuilder fields={fields} initialValues={initialValues} onSubmit={handleSubmit} gap='gap-3' />
      <ComponentTable columns={tableColumns} rows={tableRows} onRowDoubleClick={(row) => {
        handleEditItem(row, true, row.Id);
      }} />
    </Box>
  );
}
